import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLaunchDarkly } from "integrations/launch-darkly/LaunchDarklyContext";
import { FeatureFlags } from "integrations/launch-darkly/utils";
import { useAuth } from "./AuthContext";

interface IFeatureFlagContext {
  features: FeatureFlags;
}

export const FeatureFlagContext = createContext<IFeatureFlagContext>(
  undefined as never,
);

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { authUser } = useAuth();
  const { features, setContext, clearContext } = useLaunchDarkly();

  useEffect(() => {
    if (!authUser) {
      clearContext();
      return;
    }
    setContext("user", {
      key: authUser.uid,
      email: authUser.email ?? undefined,
      name: authUser.displayName ?? undefined,
    });
  }, [authUser, setContext, clearContext]);

  const value = useMemo(() => ({ features }), [features]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const ctxt = useContext(FeatureFlagContext);
  if (!ctxt) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  return ctxt;
};
